/**
 * NewServicePage
 *
 * Route: /projects/:projectId/services/new
 * Optional query param: ?environment=<environmentId>
 *
 * Layout strategy:
 *  Step 1  — two-panel: catalog browser (left) + service preview (right)
 *  Steps 2+ — full-width centered column: prominent step bar + focused form
 *
 * Animation strategy (Framer Motion):
 *  - Browse ↔ configure: fade + slide Y (AnimatePresence mode="wait")
 *  - Configure ↔ env-vars: directional slide X (AnimatePresence + direction state)
 *  - Step circles: spring scale pop on activation
 *  - Connector lines: scaleX fill from left as steps complete
 *  - Context chip: fade-in-up on first appearance
 */

import { AnimatePresence, motion } from "framer-motion";
import {
  BarChart,
  Brain,
  Check,
  Code,
  Database,
  Globe,
  HardDrive,
  Layers,
  Loader2,
  Server,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  DEFAULT_SERVICE_DATA,
  EASE_OUT_EXPO,
  STEPS_NO_ENV,
  STEPS_WITH_ENV,
  ServiceConfigurationForm,
  ServiceCreationData,
  ServiceCreationStep,
  ServiceEnvVarsStep,
  ServiceTemplateSelector,
  StepBar,
  StepMeta,
} from "../components/services/service-creation";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/ui/page-header";
import { api } from "../lib/api";
import { createLogger } from "../lib/logger";
import { notify } from "../lib/notify";
import { useAppStore } from "../store/useAppStore";
import { CreateServiceData, ServiceType } from "../types";
import {
  CreateServiceFromCatalogData,
  ServiceCatalogItem,
} from "../types/service-catalog";

const log = createLogger("NewServicePage");

// ── Category helpers ──────────────────────────────────────────────────────────

const CATEGORY_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  "web-applications": Globe,
  databases: Database,
  development: Code,
  infrastructure: Server,
  "ai-ml": Brain,
  applications: Layers,
  devops: Layers,
  collaboration: Users,
  analytics: BarChart,
  "object-storage": HardDrive,
};

const CATEGORY_COLORS: Record<string, string> = {
  databases: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  cache:
    "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
  search:
    "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
  "object-storage":
    "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
  "web-applications":
    "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400",
  development:
    "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  "ai-ml": "bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400",
  analytics: "bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400",
  devops: "bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400",
  infrastructure:
    "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

function getCategoryIcon(
  cat: string
): React.ComponentType<{ className?: string }> {
  return CATEGORY_ICONS[cat] ?? Server;
}
function getCategoryColor(cat: string): string {
  return CATEGORY_COLORS[cat] ?? "bg-muted text-muted-foreground";
}

// ── Vertical step nav (steps 2+) ─────────────────────────────────────────────

const STEP_DESCRIPTIONS: Record<ServiceCreationStep, string> = {
  "select-type": "Select what to deploy",
  configure: "Name and repository",
  "env-vars": "Optional runtime variables",
};

const STEP_HEADINGS: Record<
  "configure" | "env-vars",
  { title: (name: string) => string; desc: (name: string) => string }
> = {
  configure: {
    title: (name) => `Configure ${name}`,
    desc: () => "Set your service name and connect your repository.",
  },
  "env-vars": {
    title: () => "Environment Variables",
    desc: (name) =>
      `Add runtime variables ${name} needs. You can skip this and add them later.`,
  },
};

interface VerticalStepNavProps {
  steps: StepMeta[];
  currentStep: ServiceCreationStep;
}

function VerticalStepNav({ steps, currentStep }: VerticalStepNavProps) {
  const currentIdx = steps.findIndex((s) => s.key === currentStep);
  return (
    <nav aria-label="Steps" className="flex flex-col">
      {steps.map((step, idx) => {
        const isDone = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <div key={step.key} className="flex gap-3.5">
            {/* Circle + vertical connector */}
            <div className="flex flex-col items-center">
              <motion.div
                key={`vc-${idx}-${isCurrent}`}
                initial={{ scale: isCurrent ? 0.7 : 1 }}
                animate={{
                  scale: 1,
                  backgroundColor:
                    isDone || isCurrent ? "hsl(var(--primary))" : "transparent",
                  borderColor:
                    isDone || isCurrent
                      ? "hsl(var(--primary))"
                      : "hsl(var(--border))",
                  color:
                    isDone || isCurrent
                      ? "hsl(var(--primary-foreground))"
                      : "hsl(var(--muted-foreground))",
                }}
                transition={{
                  scale: { type: "spring", stiffness: 500, damping: 18 },
                  backgroundColor: { type: "tween", duration: 0.25 },
                  borderColor: { type: "tween", duration: 0.25 },
                  color: { type: "tween", duration: 0.2 },
                }}
                className={[
                  "h-7 w-7 rounded-full flex items-center justify-center",
                  "text-xs font-bold border-2 shrink-0",
                  isCurrent ? "ring-4 ring-primary/15" : "",
                ].join(" ")}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {isDone ? (
                    <motion.span
                      key="check"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 20,
                      }}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </motion.span>
                  ) : (
                    <motion.span
                      key={`n${idx}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.1 }}
                    >
                      {idx + 1}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>
              {/* Connector line */}
              {idx < steps.length - 1 && (
                <div className="relative w-0.5 flex-1 my-1.5 bg-border rounded-full overflow-hidden min-h-[28px]">
                  <motion.div
                    className="absolute inset-0 bg-primary origin-top rounded-full"
                    initial={false}
                    animate={{ scaleY: idx < currentIdx ? 1 : 0 }}
                    transition={{ duration: 0.35, ease: EASE_OUT_EXPO }}
                  />
                </div>
              )}
            </div>
            {/* Label + description */}
            <div className={idx < steps.length - 1 ? "pb-7" : ""}>
              <motion.p
                animate={{
                  color: isCurrent
                    ? "hsl(var(--foreground))"
                    : isDone
                      ? "hsl(var(--muted-foreground))"
                      : "hsl(var(--muted-foreground) / 0.5)",
                }}
                transition={{ duration: 0.2 }}
                className={`text-sm leading-none mt-0.5 ${isCurrent ? "font-semibold" : "font-medium"}`}
              >
                {step.label}
              </motion.p>
              <p
                className={`text-xs mt-1 leading-snug transition-colors duration-200 ${
                  isCurrent
                    ? "text-muted-foreground"
                    : "text-muted-foreground/40"
                }`}
              >
                {STEP_DESCRIPTIONS[step.key]}
              </p>
            </div>
          </div>
        );
      })}
    </nav>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function NewServicePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const environmentId = searchParams.get("environment") ?? undefined;
  const {
    createService,
    fetchServiceCatalog,
    searchServiceTemplates,
    createServiceFromTemplate,
    serviceCatalog,
    serviceCatalogLoading,
  } = useAppStore();
  const [step, setStep] = useState<ServiceCreationStep>("select-type");
  const [selectedTemplate, setSelectedTemplate] =
    useState<ServiceCatalogItem | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ServiceCatalogItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [serviceData, setServiceData] =
    useState<ServiceCreationData>(DEFAULT_SERVICE_DATA);
  const [configErrors, setConfigErrors] = useState<{
    name?: string;
    repo?: string;
  }>({});
  // 1 = forward (configure → env-vars), -1 = backward (env-vars → configure)
  const [slideDirection, setSlideDirection] = useState(1);
  const requiresRepo =
    selectedTemplate?.defaultConfig?.requiresRepository ?? false;
  const steps = requiresRepo ? STEPS_WITH_ENV : STEPS_NO_ENV;
  const isBrowseStep = step === "select-type";
  // ── Catalog loading ───────────────────────────────────────────────────────
  useEffect(() => {
    if (Object.keys(serviceCatalog).length === 0 && !serviceCatalogLoading) {
      fetchServiceCatalog().catch((err) =>
        log.error("Failed to fetch service catalog", err)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ── Debounced search ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const id = setTimeout(async () => {
      try {
        setSearchResults(await searchServiceTemplates(searchQuery));
      } catch {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [searchQuery, searchServiceTemplates]);
  // ── Template selection ────────────────────────────────────────────────────
  const handleTemplateSelect = useCallback((template: ServiceCatalogItem) => {
    if (template.isComingSoon) return;
    setSelectedTemplate(template);
    setServiceData((prev) => ({
      ...DEFAULT_SERVICE_DATA,
      name: template.displayName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, ""),
      framework: template.defaultConfig?.framework ?? "",
      buildCommand: template.defaultConfig?.buildCommand ?? "",
      startCommand: template.defaultConfig?.startCommand ?? "",
      port: template.defaultConfig?.defaultPort?.toString() ?? "",
      customCredentials:
        template.defaultConfig?.connectionConfig?.defaultCredentials ?? {},
      envVars: prev.envVars,
    }));
    setConfigErrors({});
    setSlideDirection(1);
    setStep("configure");
  }, []);
  // ── Step navigation with direction ────────────────────────────────────────
  const goBack = (to: ServiceCreationStep) => {
    setSlideDirection(-1);
    setStep(to);
  };
  const handleConfigureContinue = () => {
    const errors: typeof configErrors = {};
    if (!serviceData.name.trim()) errors.name = "Service name is required";
    if (requiresRepo && !serviceData.repository.url.trim())
      errors.repo = "Repository URL is required";
    if (Object.keys(errors).length > 0) {
      setConfigErrors(errors);
      return;
    }
    setConfigErrors({});
    setSlideDirection(1);
    setStep("env-vars");
  };
  // ── Create service ────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!selectedTemplate || !serviceData.name || !projectId) {
      notify.error("Missing information", {
        description: "Please fill in all required fields.",
      });
      return;
    }
    if (requiresRepo && !serviceData.repository.url) {
      notify.error("Missing information", {
        description: "Repository URL is required.",
      });
      return;
    }
    try {
      setIsCreating(true);
      let serviceResponse;
      if (selectedTemplate.id.startsWith("legacy-")) {
        const data: CreateServiceData = {
          name: serviceData.name,
          type: selectedTemplate.type as ServiceType,
          repository: serviceData.repository.url
            ? {
                url: serviceData.repository.url,
                branch: serviceData.repository.branch || "main",
                rootDirectory:
                  serviceData.repository.rootDirectory || undefined,
                git_account_id:
                  serviceData.repository.git_account_id || undefined,
              }
            : undefined,
          framework: serviceData.framework || undefined,
          buildCommand: serviceData.buildCommand || undefined,
          startCommand: serviceData.startCommand || undefined,
          environmentId,
          customCredentials: serviceData.customCredentials,
        };
        serviceResponse = await createService(projectId, data);
      } else {
        const data: CreateServiceFromCatalogData = {
          name: serviceData.name,
          templateType: selectedTemplate.type,
          repository: serviceData.repository.url
            ? {
                url: serviceData.repository.url,
                branch: serviceData.repository.branch || "main",
                rootDirectory:
                  serviceData.repository.rootDirectory || undefined,
                git_account_id:
                  serviceData.repository.git_account_id || undefined,
              }
            : undefined,
          templateConfig: {
            framework: serviceData.framework || undefined,
            buildCommand: serviceData.buildCommand || undefined,
            startCommand: serviceData.startCommand || undefined,
          },
          environmentId,
          customCredentials: serviceData.customCredentials,
        };
        serviceResponse = await createServiceFromTemplate(projectId, data);
      }
      const envVars = serviceData.envVars ?? {};
      if (Object.keys(envVars).length > 0) {
        try {
          await api.env.update(
            serviceResponse.service.id,
            envVars,
            environmentId ?? "development"
          );
        } catch (envErr) {
          log.error("Failed to save initial env vars", envErr);
          notify.warning("Service created", {
            description:
              "Environment variables could not be saved — add them in the Environment Variables tab.",
          });
        }
      }
      if (serviceResponse.deployment?.id) {
        navigate(
          `/projects/${projectId}/deployments/${serviceResponse.deployment.id}`
        );
      } else {
        navigate(`/projects/${projectId}`);
      }
    } catch (err) {
      log.error("Failed to create service", err);
      notify.error("Something went wrong", {
        description: "Failed to create service. Please try again.",
      });
    } finally {
      setIsCreating(false);
    }
  };
  // ── Derived ───────────────────────────────────────────────────────────────
  const TemplateIcon = selectedTemplate
    ? getCategoryIcon(selectedTemplate.category)
    : null;
  const templateIconColor = selectedTemplate
    ? getCategoryColor(selectedTemplate.category)
    : "";
  // Footer actions (only used for steps 2+)
  const isLastStep =
    step === "env-vars" || (step === "configure" && !requiresRepo);
  const primaryLabel =
    isLastStep && selectedTemplate
      ? `Deploy ${selectedTemplate.displayName}`
      : "Continue →";
  const primaryAction =
    step === "configure"
      ? requiresRepo
        ? handleConfigureContinue
        : handleCreate
      : handleCreate;
  const footerBackAction =
    step === "configure"
      ? () => goBack("select-type")
      : () => goBack("configure");
  return (
    <div className="space-y-5">
      {/* Page header */}
      <PageHeader
        eyebrow="Project"
        title="Add a service"
        description="Browse the catalog and deploy it to this project."
        actions={
          <Button variant="soft" size="sm" asChild>
            <Link to={`/projects/${projectId}`}>← Back to project</Link>
          </Button>
        }
      />
      {/* Unified card — all 3 steps live inside */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: EASE_OUT_EXPO }}
        className="bg-card rounded-xl  overflow-hidden flex flex-col md:flex-row w-full"
        /* Viewport minus the top bar, page gutters and the PageHeader. */
        style={{ height: "calc(100vh - 16rem)" }}
      >
        {/* Mobile: step bar at top (steps 2+ only) */}
        {!isBrowseStep && (
          <div className="md:hidden shrink-0 border-b bg-muted/20 px-4 py-3">
            <StepBar steps={steps} currentStep={step} />
          </div>
        )}
        {/* Desktop: left sidebar */}
        <div className="hidden md:flex w-56 shrink-0 bg-muted/30 border-r flex-col p-6 gap-6 overflow-y-auto">
          {/* Context section — changes between browse and configure */}
          <AnimatePresence mode="wait" initial={false}>
            {isBrowseStep ? (
              <motion.div
                key="browse-ctx"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <p className="text-h5">Choose a service</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Browse the catalog and pick what to deploy
                </p>
              </motion.div>
            ) : selectedTemplate ? (
              <motion.div
                key="configure-ctx"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="space-y-3"
              >
                <div className="flex items-center gap-2.5">
                  {TemplateIcon && (
                    <div
                      className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${templateIconColor}`}
                    >
                      <TemplateIcon className="h-5 w-5" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold leading-tight truncate">
                      {selectedTemplate.displayName}
                    </p>
                    {selectedTemplate.version && (
                      <p className="text-xs text-muted-foreground">
                        {selectedTemplate.version}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSlideDirection(-1);
                    setStep("select-type");
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  ← Change service
                </button>
                <div className="h-px bg-border" />
              </motion.div>
            ) : null}
          </AnimatePresence>
          {/* Step nav — always visible */}
          <VerticalStepNav
            steps={isBrowseStep ? STEPS_WITH_ENV : steps}
            currentStep={step}
          />
        </div>
        {/* Right panel: content + optional footer */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {/* Animated content per step */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step}
              initial={{
                opacity: 0,
                x: isBrowseStep ? 0 : slideDirection * 28,
              }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: isBrowseStep ? 0 : slideDirection * -28 }}
              transition={{ duration: 0.22, ease: EASE_OUT_EXPO }}
              className={
                step === "configure"
                  ? "flex-1 flex flex-col overflow-hidden min-h-0"
                  : "flex-1 overflow-y-auto"
              }
            >
              {/* Step 1: service catalog */}
              {step === "select-type" && (
                <ServiceTemplateSelector
                  serviceCatalog={serviceCatalog}
                  serviceCatalogLoading={serviceCatalogLoading}
                  searchResults={searchResults}
                  selectedCategory={selectedCategory}
                  onCategoryChange={setSelectedCategory}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  onTemplateSelect={handleTemplateSelect}
                  selectedTemplate={selectedTemplate}
                />
              )}
              {/* Step 2: configure */}
              {step === "configure" && selectedTemplate && (
                <>
                  <div className="px-6 pt-6 md:px-8 md:pt-8 shrink-0">
                    <h2 className="text-h4 leading-tight">
                      {STEP_HEADINGS.configure.title(
                        selectedTemplate.displayName
                      )}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1 mb-6">
                      {STEP_HEADINGS.configure.desc(
                        selectedTemplate.displayName
                      )}
                    </p>
                  </div>
                  <div className="flex-1 min-h-0 overflow-hidden px-6 pb-4 md:px-8">
                    <ServiceConfigurationForm
                      selectedTemplate={selectedTemplate}
                      serviceData={serviceData}
                      onServiceDataChange={(data) => {
                        setServiceData(data);
                        if (data.name)
                          setConfigErrors((e) => ({ ...e, name: undefined }));
                        if (data.repository?.url)
                          setConfigErrors((e) => ({ ...e, repo: undefined }));
                      }}
                      onBack={() => goBack("select-type")}
                      onContinue={
                        requiresRepo ? handleConfigureContinue : undefined
                      }
                      onCreate={handleCreate}
                      isCreating={isCreating}
                      embedded
                    />
                  </div>
                  <AnimatePresence>
                    {(configErrors.name || configErrors.repo) && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="shrink-0 px-6 md:px-8 pb-2 space-y-1"
                      >
                        {configErrors.name && (
                          <p className="text-xs text-destructive">
                            {configErrors.name}
                          </p>
                        )}
                        {configErrors.repo && (
                          <p className="text-xs text-destructive">
                            {configErrors.repo}
                          </p>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
              {/* Step 3: env vars */}
              {step === "env-vars" && selectedTemplate && (
                <div className="px-6 pt-6 pb-4 md:px-8 md:pt-8">
                  <div className="mb-6">
                    <h2 className="text-h4 leading-tight">
                      {STEP_HEADINGS["env-vars"].title(
                        selectedTemplate.displayName
                      )}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      {STEP_HEADINGS["env-vars"].desc(
                        selectedTemplate.displayName
                      )}
                    </p>
                  </div>
                  <ServiceEnvVarsStep
                    selectedTemplate={selectedTemplate}
                    envVars={serviceData.envVars ?? {}}
                    onEnvVarsChange={(envVars) =>
                      setServiceData((d) => ({ ...d, envVars }))
                    }
                    onBack={() => goBack("configure")}
                    onCreate={handleCreate}
                    isCreating={isCreating}
                    embedded
                  />
                </div>
              )}
            </motion.div>
          </AnimatePresence>
          {/* Pinned footer — steps 2+ only */}
          {!isBrowseStep && selectedTemplate && (
            <div className="shrink-0 border-t bg-background/95 backdrop-blur-sm px-6 md:px-8 py-4 flex items-center justify-between gap-3">
              <button
                onClick={footerBackAction}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={primaryAction}
                disabled={isCreating}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground  hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {isCreating && <Loader2 className="h-4 w-4 animate-spin" />}
                {isCreating ? "Deploying…" : primaryLabel}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
